// PR6 3c — the mobile share flow as a DS BottomSheet (ui_kits/mobile/Mobile.jsx's MShare):
// VisibilityPicker + LinkField, plus the native share sheet via navigator.share. F3's
// protection options (password / expiry / view limit) aren't merged, so this covers only what
// §4 actually serves today — the same subset ShareDialog.tsx (the desktop dialog) covers,
// read and written through the same endpoints.
import { useCallback, useEffect, useState } from "react";
import { BottomSheet, Button, Icon, LinkField, VisibilityPicker } from "@grackle/ui";
import { api, ApiFail } from "../../api/client";
import type { GrackleVisibility, ShareState } from "../../api/types";
import "./MobileShareSheet.css";

type PickerVisibility = "private" | "people" | "team" | "org" | "public";

const TO_PICKER: Record<GrackleVisibility, PickerVisibility> = {
  private: "private",
  specific: "people",
  team: "team",
  org: "org",
  public: "public",
};

const FROM_PICKER: Record<PickerVisibility, GrackleVisibility> = {
  private: "private",
  people: "specific",
  team: "team",
  org: "org",
  public: "public",
};

/** Strips the scheme the server includes (`public_base_url`) — LinkField's contract is a bare
 * domain + path, matching the DS sample (`grackle.app/v/...`). */
function bareUrl(url: string): string {
  return url.replace(/^https?:\/\//, "");
}

export default function MobileShareSheet({
  grackleId,
  title,
  meta,
  onClose,
}: {
  grackleId: string;
  title: string;
  /** e.g. "6:42" — shown next to the title the way the DS sheet does. */
  meta?: string;
  onClose: () => void;
}) {
  const [state, setState] = useState<ShareState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await api<ShareState>("GET", `/grackles/${grackleId}/share`));
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load sharing settings.");
    }
  }, [grackleId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function chooseVisibility(picked: string) {
    const next = FROM_PICKER[picked as PickerVisibility] ?? "org";
    if (!state || state.visibility === next || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api<void>("PATCH", `/grackles/${grackleId}`, { visibility: next });
      await load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't update visibility.");
    } finally {
      setBusy(false);
    }
  }

  async function createLink() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api<{ token: string; url: string }>("POST", `/grackles/${grackleId}/link`);
      await load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't create the link.");
    } finally {
      setBusy(false);
    }
  }

  const link = state?.link ?? null;
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  async function shareVia() {
    if (!link || !canNativeShare) return;
    try {
      await navigator.share({ title, url: link.url });
    } catch {
      // The share sheet's own cancel is not an error (AbortError); the link is still visible.
    }
  }

  return (
    <BottomSheet onClose={onClose}>
      <div className="msheet__head">
        <span className="msheet__head-icon">
          <Icon name="check-circle" fill size={22} />
        </span>
        <div>
          <div className="gk-h4">Share</div>
          <div className="gk-sm msheet__head-meta">{meta ? `${title} · ${meta}` : title}</div>
        </div>
      </div>

      {error ? <div className="msheet__error">{error}</div> : null}

      {link ? (
        <LinkField size="sm" url={bareUrl(link.url)} />
      ) : state?.visibility === "public" ? (
        <Button onClick={() => void createLink()} disabled={busy} icon="link-simple">
          Create link
        </Button>
      ) : null}

      <div>
        <div className="msheet__label">Who can watch</div>
        <VisibilityPicker
          layout="list"
          value={state ? TO_PICKER[state.visibility] : undefined}
          onChange={chooseVisibility}
        />
      </div>

      {canNativeShare && link ? (
        <Button variant="inverse" size="lg" icon="link-simple" block onClick={() => void shareVia()}>
          Share via…
        </Button>
      ) : null}
    </BottomSheet>
  );
}
